import { ChevronDownIcon } from '../../../shared/ui/icons';
import { FilterBar, FilterChip, FilterMenu, FilterSearch } from '../../../shared/ui/filter-bar';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../shared/ui/menu';
import type { IFlowFilters, TFlowStateFilter } from '../../../entities/flow';

const STATE_OPTIONS: readonly { value: TFlowStateFilter; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'disabled', label: 'Disabled' },
];

interface IFiltersBarProps {
  filters: IFlowFilters;
  onFilters: (next: IFlowFilters) => void;
  sourceOptions: readonly { value: string; label: string }[];
  hasProblemsCount: number | undefined;
}

export const FlowFiltersBar = ({
  filters,
  onFilters,
  sourceOptions,
  hasProblemsCount,
}: IFiltersBarProps) => {
  // Any change of what is asked for starts back at the first page.
  const set = (patch: Partial<IFlowFilters>) =>
    onFilters({ ...filters, ...patch, cursor: undefined });

  const startsFromLabel = !filters.startsFrom
    ? 'any'
    : filters.startsFrom === 'schedule'
      ? 'Schedule'
      : filters.startsFrom === 'manual'
        ? 'Manual'
        : (sourceOptions.find((option) => option.value === filters.startsFrom)?.label ??
          filters.startsFrom);

  return (
    <FilterBar>
      <Menu>
        <MenuTrigger asChild>
          <FilterChip>
            Starts from: {startsFromLabel}
            <ChevronDownIcon size={11} />
          </FilterChip>
        </MenuTrigger>
        <MenuContent align="start">
          <MenuItem selected={!filters.startsFrom} onSelect={() => set({ startsFrom: undefined })}>
            Any
          </MenuItem>
          <MenuItem
            selected={filters.startsFrom === 'schedule'}
            onSelect={() => set({ startsFrom: 'schedule' })}
          >
            Schedule
          </MenuItem>
          <MenuItem
            selected={filters.startsFrom === 'manual'}
            onSelect={() => set({ startsFrom: 'manual' })}
          >
            Manual
          </MenuItem>
          {sourceOptions.length > 0 && <MenuSeparator />}
          {sourceOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={filters.startsFrom === option.value}
              onSelect={() => set({ startsFrom: option.value })}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>

      <FilterMenu
        label="State"
        value={filters.state}
        options={STATE_OPTIONS}
        onChange={(state) => set({ state })}
      />

      <FilterChip
        on={filters.hasProblems}
        onClick={() => set({ hasProblems: !filters.hasProblems || undefined })}
        aria-pressed={Boolean(filters.hasProblems)}
      >
        Has problems{typeof hasProblemsCount === 'number' ? ` · ${hasProblemsCount}` : ''}
      </FilterChip>

      <FilterSearch placeholder="Find a flow" value={filters.q} onChange={(q) => set({ q })} />
    </FilterBar>
  );
};
